<script lang="ts">
	import '../app.css';
	import { QueryClientProvider } from '@tanstack/svelte-query';
	import dayjs from 'dayjs';
	import advancedFormat from 'dayjs/plugin/advancedFormat';
	import customParseFormat from 'dayjs/plugin/customParseFormat';
	import utc from 'dayjs/plugin/utc';
	import { onMount } from 'svelte';
	import type { PageData } from './$types';
	import { Config } from '#lib/constants/config.js';
	import { LiveDropInfoContext } from '#lib/features/live-drop-info/LiveDropInfoContext/index.js';
	import { Footer } from '#lib/features/navigation/components/Footer/index.js';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { TagManagers } from '#lib/features/tag-managers/components/TagManagers/index.js';
	import { isOnProductionOnly } from '#lib/utils/env/isOnProductionOnly/isOnProductionOnly.js';
	import { hideSuccess } from '#lib/websites/register-interest/pages/RegisterInterest/runes/showSuccess/showSuccess.svelte.js';
	import { browser } from '$app/env';
	import { PUBLIC_APP_ENV } from '$app/env/public';
	import { afterNavigate, beforeNavigate, goto } from '$app/navigation';
	import { page, updated } from '$app/state';
	import { MarkdownContext } from '$global/components/Markdown/components/MarkdownContext';
	import { Seo } from '$global/features/seo/components/Seo';
	import { ensureDeviceId } from '$global/utils/ensureDeviceId';
	import { fetchWithRetry } from '$global/utils/fetchWithRetry/fetchWithRetry';
	import { getImageUrl } from '$global/utils/getImageUrl';
	import { isShallowUpdate } from '$global/utils/isShallowUpdate/isShallowUpdate';
	import { scrollToTop } from '$global/utils/scrollToTop';
	import { setBreakpointGlobalProperties } from '$global/utils/track';

	// On error pages the failing load's data never arrives, so the URL param is
	// the fallback authority — without it the footer would show on app-mode
	// (global_nav=0) error pages.
	let showGlobalNav = $derived(
		page.data.showGlobalNav ?? page.url.searchParams.get('global_nav') !== '0'
	);

	beforeNavigate((e) => {
		// A shallow update only changes the URL or history state of the page being
		// viewed: none of the handling below applies to it.
		if (isShallowUpdate(e)) return;

		if (e.to?.route.id == GlobalRoutes.RegisterInterest) {
			hideSuccess();
		}

		if (updated && e.willUnload && e.to?.url) {
			location.href = e.to.url.href;
		}

		if (e.type === 'popstate') {
			if (
				e?.to?.route?.id?.endsWith('/order-confirmed') ||
				e?.to?.route?.id?.endsWith('/confirmed')
			) {
				goto(e?.from?.url?.pathname || GlobalRoutes.Home);
			}
		}
	});

	afterNavigate((e) => {
		if (isShallowUpdate(e)) return;

		const hasAnchorLink = e?.to?.url.hash;

		if (hasAnchorLink && e.type === 'link') {
			return;
		}

		if (
			e.type === 'link' ||
			(e.type === 'popstate' &&
				(e?.from?.route?.id?.endsWith('/order-confirmed') ||
					e?.to?.route?.id?.endsWith('/checkout') ||
					e?.to?.route?.id?.endsWith('/invoice') ||
					e?.from?.route?.id?.endsWith('/confirmed')))
		) {
			scrollToTop();
		}
	});

	interface Props {
		data: PageData;
		children?: import('svelte').Snippet;
	}

	let { data, children }: Props = $props();

	let authenticated = $state(
		PUBLIC_APP_ENV === 'local' || !PUBLIC_APP_ENV || isOnProductionOnly()
			? true
			: null
	);

	onMount(async () => {
		setBreakpointGlobalProperties();
		ensureDeviceId();

		if (browser && authenticated === null) {
			const response = await fetchWithRetry('/api/check-user');
			authenticated = response.ok;
		}
	});

	dayjs.extend(utc);
	dayjs.extend(customParseFormat);
	dayjs.extend(advancedFormat);
</script>

{#if authenticated !== null}
	{#if authenticated}
		<TagManagers />
		<LiveDropInfoContext>
			<MarkdownContext domain={Config.Domain}>
				<Seo
					{getImageUrl}
					facebookAppId={isOnProductionOnly()
						? '3261122710819288'
						: '593836114982905'}
				/>
				<QueryClientProvider client={data.queryClient}>
					{@render children?.()}
					{#if showGlobalNav}
						<Footer />
					{/if}
				</QueryClientProvider>
			</MarkdownContext>
		</LiveDropInfoContext>
	{:else}
		<!-- The password gate only exists outside production (`authenticated` is
		always true there), so its login form and schemas are fetched on demand
		instead of shipping in every visitor's root bundle. -->
		{#await import('#lib/websites/login/pages/LoginPage/index.js') then { LoginPage }}
			<LoginPage />
		{/await}
	{/if}
{/if}
